import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import { Input } from 'src/@/components/ui/input';
import { Textarea } from 'src/@/components/ui/textarea';
import { ButtonLoading } from 'src/@/components/use/ButtonLoading';
import { toast } from 'src/@/components/ui/use-toast';
import mktAccountsService from 'src/services/mktAccountsService';
import { abrirMetaOAuth, unirEscopos, ESCOPOS_MESSENGER, ESCOPOS_FACEBOOK } from './metaOAuth';
import useAuth from 'src/hooks/useAuth';
import { SOCIAL_PROFILE_DESIGN as d } from './SocialProfile.design';
import SocialMetricsPanel from './SocialMetricsPanel';

const ABOUT_MAX = 100;

function emptyForm() {
    return {
        about: '',
        description: '',
        website: '',
        email: '',
        phone: '',
    };
}

function apiErrorMessage(err, fallback) {
    return err?.response?.data?.message
        || err?.message
        || fallback;
}

function isValidHttpUrl(value) {
    try {
        const u = new URL(String(value).trim());
        if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
        return Boolean(u.hostname) && u.hostname.includes('.');
    } catch {
        return false;
    }
}

function isValidEmail(value) {
    const email = String(value || '').trim();
    if (!email) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function FacebookPageProfile() {
    const { t } = useTranslation(['settings', 'common']);
    const navigate = useNavigate();
    const { cliente } = useAuth();
    const [searchParams] = useSearchParams();
    const accountId = searchParams.get('accountId');
    const fileInputRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [error, setError] = useState(null);
    // Campos de no da Pagina — nao exigem read_insights, entao renderizam
    // mesmo enquanto o escopo de insights nao sai do App Review.
    const [meta, setMeta] = useState({ name: '', category: '', fan_count: 0 });
    const [pictureUrl, setPictureUrl] = useState('');
    const [needsMetadataScope, setNeedsMetadataScope] = useState(false);
    const [form, setForm] = useState(emptyForm());

    const voltar = useCallback(() => {
        navigate('/dashboard/configuracoes/contas-sociais');
    }, [navigate]);

    const carregar = useCallback(async () => {
        if (!accountId) {
            setError(t('socialAccounts.profile.accountMissing'));
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const res = await mktAccountsService.getFacebookPageProfile(accountId);
            if (res?.error) throw new Error(res.message || t('socialAccounts.profile.loadFailed'));
            const profile = res.data?.profile || {};
            setNeedsMetadataScope(Boolean(res.data?.needs_metadata_scope));
            // Campos de estatistica repassados COMO VIERAM: o backend ja omite
            // os que a Graph nao devolveu, e defaultar para 0 aqui traria de
            // volta os tiles falsos ("0 check-ins" numa Pagina sem o campo).
            const {
                followers_count, rating_count, overall_star_rating, talking_about_count,
                were_here_count, checkins, new_like_count, engagement_count,
                verification_status, page_link,
            } = profile;
            setMeta({
                name: profile.name || '',
                category: profile.category || '',
                fan_count: profile.fan_count || 0,
                followers_count, rating_count, overall_star_rating, talking_about_count,
                were_here_count, checkins, new_like_count, engagement_count,
                verification_status, page_link,
            });
            setPictureUrl(profile.profile_picture_url || '');
            setForm({
                about: profile.about || '',
                description: profile.description || '',
                website: profile.website || '',
                email: profile.email || '',
                phone: profile.phone || '',
            });
        } catch (err) {
            setError(apiErrorMessage(err, t('socialAccounts.profile.metaError')));
        } finally {
            setLoading(false);
        }
    }, [accountId, t]);

    useEffect(() => {
        carregar();
    }, [carregar]);

    const setField = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const handleSalvar = async () => {
        if (!accountId) return;

        const website = String(form.website || '').trim();
        if (website && !isValidHttpUrl(website)) {
            const msg = t('socialAccounts.profile.invalidWebsiteDesc');
            setError(msg);
            toast({ title: t('socialAccounts.profile.invalidWebsite'), description: msg, variant: 'destructive' });
            return;
        }

        const email = String(form.email || '').trim();
        if (email && !isValidEmail(email)) {
            const msg = t('socialAccounts.profile.invalidEmailDesc');
            setError(msg);
            toast({ title: t('socialAccounts.profile.invalidEmail'), description: msg, variant: 'destructive' });
            return;
        }

        const data = {};
        const about = String(form.about || '').trim();
        const description = String(form.description || '').trim();
        const phone = String(form.phone || '').trim();

        if (about) data.about = about;
        if (description) data.description = description;
        if (website) data.website = website;
        if (email) data.email = email;
        if (phone) data.phone = phone;

        if (Object.keys(data).length === 0) {
            const msg = t('socialAccounts.profile.nothingToSaveDesc');
            setError(msg);
            toast({ title: t('socialAccounts.profile.nothingToSave'), description: msg, variant: 'destructive' });
            return;
        }

        setSaving(true);
        setError(null);
        try {
            const res = await mktAccountsService.updateFacebookPageProfile(accountId, data);
            if (res?.error) throw new Error(res.message || t('socialAccounts.profile.saveFailed'));
            toast({
                title: t('socialAccounts.profile.profileUpdated'),
                description: t('socialAccounts.profile.profileUpdatedDesc'),
            });
            await carregar();
        } catch (err) {
            const msg = apiErrorMessage(err, t('socialAccounts.profile.metaError'));
            setError(msg);
            toast({ title: t('socialAccounts.profile.saveError'), description: msg, variant: 'destructive' });
        } finally {
            setSaving(false);
        }
    };

    const handlePhotoChange = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || !accountId) return;

        if (!['image/jpeg', 'image/png', 'image/pjpeg'].includes(file.type)) {
            toast({
                title: t('socialAccounts.profile.invalidFormat'),
                description: t('socialAccounts.profile.invalidFormatDesc'),
                variant: 'destructive',
            });
            return;
        }

        setUploadingPhoto(true);
        setError(null);
        try {
            const res = await mktAccountsService.updateFacebookPagePicture(accountId, file);
            if (res?.error) throw new Error(res.message || t('socialAccounts.profile.uploadFailed'));
            if (res?.data?.profile_picture_url) setPictureUrl(res.data.profile_picture_url);
            else await carregar();
            toast({
                title: t('socialAccounts.profile.photoUpdated'),
                description: t('socialAccounts.facebook.photoUpdatedDesc'),
            });
        } catch (err) {
            const msg = apiErrorMessage(err, t('socialAccounts.profile.metaError'));
            setError(msg);
            toast({ title: t('socialAccounts.profile.uploadError'), description: msg, variant: 'destructive' });
        } finally {
            setUploadingPhoto(false);
        }
    };

    if (!accountId) {
        return (
            <div className={d.page.container}>
                <div className={d.page.topBar}>
                    <button type="button" className={d.page.backButton} onClick={voltar}>
                        <Iconify icon="eva:arrow-back-outline" width={18} />
                        {t('common:buttons.back')}
                    </button>
                    <p className={d.page.headerTitle}>{t('socialAccounts.facebook.title')}</p>
                </div>
                <div className={d.page.errorBanner}>
                    <Iconify icon="eva:alert-circle-outline" width={16} />
                    {t('socialAccounts.profile.accountMissingHint')}
                </div>
            </div>
        );
    }

    return (
        <div className={d.page.container}>
            <div className={d.page.topBar}>
                <button type="button" className={d.page.backButton} onClick={voltar}>
                    <Iconify icon="eva:arrow-back-outline" width={18} />
                    {t('common:buttons.back')}
                </button>
                <p className={`${d.page.headerTitle} flex-1`}>{t('socialAccounts.facebook.title')}</p>
                <button
                    type="button"
                    className={d.btn.reload}
                    onClick={() => navigate('/dashboard/marketing/publicacoes')}
                >
                    <Iconify icon="eva:paper-plane-outline" width={14} />
                    {t('socialAccounts.header.goToPosts')}
                </button>
                <button type="button" className={d.btn.reload} onClick={carregar} disabled={loading}>
                    <Iconify icon={loading ? 'eos-icons:loading' : 'eva:refresh-outline'} width={14} />
                    {t('socialAccounts.profile.reload')}
                </button>
            </div>

            {error && (
                <div className={d.page.errorBanner}>
                    <Iconify icon="eva:alert-circle-outline" width={16} />
                    {error}
                </div>
            )}

            {needsMetadataScope && !loading && (
                <div className="mx-4 mt-3 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                    <Iconify icon="eva:lock-outline" width={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-amber-900">{t('socialAccounts.facebook.metadataScopeTitle')}</p>
                        <p className="text-[11px] text-amber-800 mt-0.5">
                            {t('socialAccounts.facebook.metadataScopeDesc')}
                        </p>
                    </div>
                    <button
                        type="button"
                        className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded-lg whitespace-nowrap"
                        onClick={() => abrirMetaOAuth(cliente?._id, {
                            // pages_manage_metadata pertence aos grupos messenger e
                            // facebook; sem a lista de contas aqui, a uniao dos dois
                            // cobre ambos os usos sem re-pedir escopos de anuncios.
                            scopes: unirEscopos(ESCOPOS_MESSENGER, ESCOPOS_FACEBOOK),
                            returnTo: '/dashboard/configuracoes/contas-sociais',
                        })}
                    >
                        {t('socialAccounts.facebook.updatePermissions')}
                    </button>
                </div>
            )}

            {loading ? (
                <div className={d.loading}>
                    <Iconify icon="eos-icons:loading" width={24} className="text-violet-600" />
                    {t('socialAccounts.profile.loading')}
                </div>
            ) : (
                <>
                    <div className={d.page.body}>
                        <div className={d.page.panel}>
                            <div className={d.identity.row}>
                                <div className={d.identity.photo}>
                                    {pictureUrl ? (
                                        <img src={pictureUrl} alt="" className={d.identity.preview} />
                                    ) : (
                                        <div className={d.identity.previewFallback}>
                                            <Iconify icon="mdi:facebook" width={24} className="text-blue-600" />
                                        </div>
                                    )}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/jpeg,image/png"
                                        className="hidden"
                                        onChange={handlePhotoChange}
                                    />
                                    <button
                                        type="button"
                                        className={d.identity.changeBtn}
                                        disabled={uploadingPhoto}
                                        onClick={() => fileInputRef.current?.click()}
                                        title={t('socialAccounts.profile.changePhoto')}
                                        aria-label={t('socialAccounts.profile.changePhoto')}
                                    >
                                        <Iconify
                                            icon={uploadingPhoto ? 'eos-icons:loading' : 'eva:camera-outline'}
                                            width={14}
                                        />
                                    </button>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className={d.identity.fields}>
                                        <div className={d.form.field}>
                                            <label className={d.form.label}>{t('socialAccounts.facebook.pageName')}</label>
                                            <Input value={meta.name} disabled className={d.form.inputReadonly} />
                                        </div>
                                        <div className={d.form.field}>
                                            <label className={d.form.label}>{t('socialAccounts.profile.category')}</label>
                                            <Input value={meta.category} disabled className={d.form.inputReadonly} />
                                        </div>
                                    </div>
                                    <p className={d.identity.hint}>
                                        <Iconify icon="eva:info-outline" width={14} className="flex-shrink-0 mt-px" />
                                        {t('socialAccounts.facebook.nameCategoryHint')}
                                    </p>
                                </div>
                            </div>

                            <SocialMetricsPanel
                                provider="facebook"
                                accountId={accountId}
                                node={meta}
                                onReconnect={() => abrirMetaOAuth(cliente?._id, {
                                    scopes: unirEscopos(ESCOPOS_MESSENGER, ESCOPOS_FACEBOOK),
                                    returnTo: '/dashboard/configuracoes/facebook-perfil',
                                })}
                            />

                            <div className={d.form.grid}>
                                <div className={d.form.fieldFull}>
                                    <div className={d.form.labelRow}>
                                        <label className={d.form.label}>{t('socialAccounts.facebook.about')}</label>
                                        <span className={d.form.counter}>{form.about.length}/{ABOUT_MAX}</span>
                                    </div>
                                    <Textarea
                                        value={form.about}
                                        maxLength={ABOUT_MAX}
                                        onChange={(e) => setField('about', e.target.value)}
                                        className={d.form.textareaSm}
                                        placeholder={t('socialAccounts.facebook.aboutPlaceholder')}
                                        rows={2}
                                    />
                                </div>

                                <div className={d.form.fieldFull}>
                                    <label className={d.form.label}>{t('socialAccounts.facebook.additionalInfo')}</label>
                                    <Textarea
                                        value={form.description}
                                        onChange={(e) => setField('description', e.target.value)}
                                        className={d.form.textarea}
                                        placeholder={t('socialAccounts.facebook.additionalInfoPlaceholder')}
                                        rows={3}
                                    />
                                </div>

                                <div className={d.form.field}>
                                    <label className={d.form.label}>{t('socialAccounts.profile.website')}</label>
                                    <Input
                                        value={form.website}
                                        onChange={(e) => setField('website', e.target.value)}
                                        className={d.form.input}
                                        placeholder={t('socialAccounts.profile.websitePlaceholder')}
                                    />
                                </div>

                                <div className={d.form.field}>
                                    <label className={d.form.label}>{t('socialAccounts.profile.email')}</label>
                                    <Input
                                        type="email"
                                        value={form.email}
                                        onChange={(e) => setField('email', e.target.value)}
                                        className={d.form.input}
                                        placeholder={t('socialAccounts.profile.emailPlaceholder')}
                                    />
                                </div>

                                <div className={d.form.field}>
                                    <label className={d.form.label}>{t('socialAccounts.profile.phone')}</label>
                                    <Input
                                        value={form.phone}
                                        onChange={(e) => setField('phone', e.target.value)}
                                        className={d.form.input}
                                        placeholder={t('socialAccounts.facebook.phonePlaceholder')}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={d.page.footer}>
                        <button type="button" className={d.btn.cancel} onClick={voltar} disabled={saving}>
                            {t('common:buttons.cancel')}
                        </button>
                        <ButtonLoading
                            type="button"
                            className={d.btn.primary}
                            onClick={handleSalvar}
                            loading={saving}
                            disabled={saving || uploadingPhoto}
                        >
                            <Iconify icon="eva:checkmark-outline" width={15} />
                            {t('common:buttons.save')}
                        </ButtonLoading>
                    </div>
                </>
            )}
        </div>
    );
}
